import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiTrash2, FiX,
  FiDollarSign, FiExternalLink,
} from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';

const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const METHOD_LABEL  = { cash: 'Cash', upi: 'UPI', bank_transfer: 'Bank Transfer', cheque: 'Cheque', other: 'Other' };

/* ── Allocate to Purchase Modal — COMMENTED OUT ──────────────────
   Uncomment this entire component and its usage at the bottom
   to restore the allocation flow from this page.

function AllocateToPurchaseModal({ payment, unallocated, vendorId, onClose, onSaved }) {
  const [q,          setQ]          = useState('');
  const [purchases,  setPurchases]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [purchaseId, setPurchaseId] = useState('');
  const [amount,     setAmount]     = useState('');
  const [saving,     setSaving]     = useState(false);

  const fetchPurchases = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get(`/vendors/${vendorId}/purchases`, {
        params: { limit: 100 },
      });
      const rows = (data.purchases || []).filter(p =>
        p.status !== 'CANCELLED' && p.outstanding > 0.001
      );
      setPurchases(rows);
    } catch {
      toast.error('Failed to load purchases');
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => { fetchPurchases(); }, [fetchPurchases]);

  const filtered = purchases.filter(p =>
    !q.trim() ||
    p.purchase_number.toLowerCase().includes(q.toLowerCase()) ||
    (p.vendor_invoice_number || '').toLowerCase().includes(q.toLowerCase())
  );

  const selected = purchases.find(p => String(p.id) === String(purchaseId));

  const handleSave = async () => {
    if (!purchaseId) return toast.error('Select a purchase');
    if (!Number(amount) || Number(amount) <= 0) return toast.error('Enter a valid amount');
    try {
      setSaving(true);
      await API.post('/purchases/allocate', {
        vendor_payment_id: payment.id,
        purchase_id:       Number(purchaseId),
        allocated_amount:  Number(amount),
      });
      toast.success('Allocated successfully');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to allocate');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 520, maxWidth: '95vw', boxShadow: '0 8px 40px rgba(0,0,0,0.15)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Allocate to Purchase</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><FiX size={18} /></button>
        </div>

        <div style={{ background: '#eef2ff', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 13 }}>
          Payment: <strong>{payment.payment_number}</strong> &nbsp;·&nbsp; Unallocated: <strong>{money(unallocated)}</strong>
        </div>

        {loading ? (
          <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af' }}>Loading purchases…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Search Purchase</label>
              <div style={{ position: 'relative' }}>
                <input
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 28px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
                  placeholder="Purchase no. or invoice no…"
                  value={q}
                  onChange={e => setQ(e.target.value)}
                />
              </div>
            </div>

            {filtered.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                No open purchases found for this vendor.
              </div>
            ) : (
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Select Purchase *</label>
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', maxHeight: 220, overflowY: 'auto' }}>
                  {filtered.map(p => (
                    <div
                      key={p.id}
                      onClick={() => { setPurchaseId(String(p.id)); setAmount(String(Math.min(p.outstanding, unallocated))); }}
                      style={{
                        padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                        background: String(p.id) === purchaseId ? '#eef2ff' : undefined,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#6366f1', fontWeight: 600 }}>{p.purchase_number}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>Outstanding: {money(p.outstanding)}</span>
                      </div>
                      <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                        {fmt(p.purchase_date)} &nbsp;·&nbsp; Total: {money(p.grand_total)}
                        {p.vendor_invoice_number && ` · Inv: ${p.vendor_invoice_number}`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selected && (
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Amount to Allocate (₹) *</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={Math.min(Number(selected.outstanding), Number(unallocated))}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14 }}
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                />
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
                  Max: {money(Math.min(Number(selected.outstanding), Number(unallocated)))}
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button className="crm-btn" onClick={onClose}>Cancel</button>
          <button className="crm-btn crm-btn--primary" onClick={handleSave} disabled={saving || !purchaseId}>
            {saving ? 'Saving…' : 'Allocate'}
          </button>
        </div>
      </div>
    </div>
  );
}
END OF ALLOCATION MODAL */

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ══════════════════════════════════════════════════════════════════ */
export default function AdminVendorPaymentDetail() {
  const { paymentId } = useParams();
  const navigate      = useNavigate();

  const [payment,     setPayment]     = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [loading,     setLoading]     = useState(true);

  // const [allocModal,  setAllocModal]  = useState(false); // ALLOCATION: uncomment to restore

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const payRes = await API.get('/vendors/payments/all', { params: { page: 1, limit: 1000 } });
      const allPayments = payRes.data.payments || [];
      const found       = allPayments.find(p => String(p.id) === String(paymentId));
      if (!found) { setLoading(false); return; }

      setPayment(found);

      // Fetch allocations for this payment via vendor's purchases
      const purchasesRes = await API.get(`/vendors/${found.vendor_id}/purchases`, { params: { limit: 200 } });
      const vendorPurchases = purchasesRes.data.purchases || [];

      const allAllocs = [];
      await Promise.all(
        vendorPurchases.map(async (pur) => {
          try {
            const ar = await API.get(`/purchases/${pur.id}/allocations`);
            const forThisPayment = (ar.data.allocations || []).filter(
              a => String(a.vendor_payment_id) === String(paymentId)
            );
            forThisPayment.forEach(a => allAllocs.push({ ...a, purchase: pur }));
          } catch {}
        })
      );

      setAllocations(allAllocs);
    } catch (err) {
      toast.error('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRemoveAllocation = async (allocId) => {
    if (!window.confirm('Remove this payment record? This will mark the purchase as unpaid again.')) return;
    try {
      await API.delete(`/purchases/allocations/${allocId}`);
      toast.success('Payment record removed');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>;
  if (!payment) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#dc2626' }}>
      Payment not found.{' '}
      <button onClick={() => navigate('/admin/vendor-payments')} style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontWeight: 600 }}>
        Back to Payments
      </button>
    </div>
  );

  const totalAllocated = allocations.reduce((s, a) => s + Number(a.allocated_amount), 0);
  const isCancelled    = payment.status === 'cancelled';

  const STATUS_BADGE = {
    paid:      { background: '#dcfce7', color: '#16a34a' },
    pending:   { background: '#fef3c7', color: '#d97706' },
    cancelled: { background: '#f3f4f6', color: '#9ca3af' },
  };

  const sectionHead = (title) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 13, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</div>
    </div>
  );

  return (
    <div style={{ padding: '28px 24px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <button onClick={() => navigate('/admin/vendor-payments')} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '7px 10px', cursor: 'pointer' }}>
          <FiArrowLeft size={16} />
        </button>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontWeight: 700, fontSize: '1.25rem', fontFamily: 'monospace' }}>
              {payment.payment_number}
            </h2>
            <span style={{ ...STATUS_BADGE[payment.status], padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>
              {payment.status}
            </span>
          </div>
          <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 4 }}>
            {payment.vendor?.company_name} &nbsp;·&nbsp; {fmt(payment.payment_date)}
          </div>
        </div>
      </div>

      {/* Stats bar — unallocated column commented out */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Payment Amount', value: money(payment.amount), color: '#374151' },
          { label: 'Allocated',      value: money(totalAllocated), color: '#16a34a' },
          /*
          ALLOCATION: uncomment the card below (and change gridTemplateColumns to 'repeat(3, 1fr)')
          to show the unallocated balance and enable the allocate button.
          { label: 'Unallocated', value: money(Number(payment.amount) - totalAllocated), color: (Number(payment.amount) - totalAllocated) > 0 ? '#d97706' : '#16a34a' },
          */
        ].map(s => (
          <div key={s.label} className="crm-card" style={{ padding: '16px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ALLOCATION: unallocated alert — uncomment to restore
      {(Number(payment.amount) - totalAllocated) > 0.001 && !isCancelled && (
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span><strong>{money(Number(payment.amount) - totalAllocated)}</strong> is unallocated. Allocate it to open purchases.</span>
          <button className="crm-btn crm-btn--primary" onClick={() => setAllocModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontSize: 13 }}>
            <FiPlus size={13} /> Allocate
          </button>
        </div>
      )} */}

      {/* Payment Information */}
      <div className="crm-card" style={{ marginBottom: 20 }}>
        {sectionHead('Payment Information')}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {[
            { label: 'Payment No.',    value: payment.payment_number, mono: true },
            { label: 'Vendor',         value: payment.vendor?.company_name },
            { label: 'Date',           value: fmt(payment.payment_date) },
            { label: 'Amount',         value: money(payment.amount) },
            { label: 'Method',           value: METHOD_LABEL[payment.payment_method] || payment.payment_method },
            { label: 'Paid From Account', value: payment.bankAccount?.account_name || '—' },
            { label: 'Reference / UTR',  value: payment.reference_number || '—' },
            { label: 'Status',           value: payment.status },
          ].map(r => (
            <div key={r.label} style={{ display: 'flex', gap: 10, fontSize: 14 }}>
              <span style={{ color: '#9ca3af', minWidth: 140 }}>{r.label}</span>
              <span style={{ fontWeight: 500, fontFamily: r.mono ? 'monospace' : 'inherit' }}>{r.value || '—'}</span>
            </div>
          ))}
        </div>
        {payment.notes && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: '#f9fafb', borderRadius: 8, fontSize: 14, color: '#6b7280' }}>
            {payment.notes}
          </div>
        )}
        <button
          onClick={() => navigate(`/admin/vendors/${payment.vendor_id}`)}
          style={{ marginTop: 12, background: 'none', border: 'none', color: '#6366f1', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 5 }}
        >
          View Vendor <FiExternalLink size={13} />
        </button>
      </div>

      {/* Allocations / Linked Purchases */}
      <div className="crm-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Linked Purchases ({allocations.length})</div>
          {/* ALLOCATION: uncomment the button below to restore allocate from this page
          {!isCancelled && (Number(payment.amount) - totalAllocated) > 0.001 && (
            <button className="crm-btn crm-btn--primary" onClick={() => setAllocModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 13 }}>
              <FiPlus size={13} /> Allocate to Purchase
            </button>
          )} */}
        </div>

        {allocations.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            <FiDollarSign size={32} style={{ marginBottom: 10, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>No purchases linked to this payment.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f3f4f6', background: '#fafafa' }}>
                {['Purchase No.', 'Purchase Date', 'Invoice No.', 'Grand Total', 'Amount Paid', ''].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allocations.map(a => {
                const pur = a.purchase || {};
                return (
                  <tr key={a.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 14px' }}>
                      <button
                        onClick={() => navigate(`/admin/purchases/${pur.id}`)}
                        style={{ background: 'none', border: 'none', color: '#6366f1', fontFamily: 'monospace', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        {pur.purchase_number} <FiExternalLink size={11} />
                      </button>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#374151' }}>{fmt(pur.purchase_date)}</td>
                    <td style={{ padding: '10px 14px', color: '#6b7280', fontFamily: 'monospace', fontSize: 13 }}>{pur.vendor_invoice_number || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>{money(pur.grand_total)}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 700, color: '#16a34a' }}>{money(a.allocated_amount)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      {!isCancelled && (
                        <button
                          onClick={() => handleRemoveAllocation(a.id)}
                          style={{ background: '#fee2e2', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}
                          title="Remove"
                        >
                          <FiTrash2 size={13} color="#dc2626" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: '#fafafa', borderTop: '2px solid #f3f4f6' }}>
                <td colSpan={4} style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 600, color: '#6b7280', fontSize: 13 }}>Total Paid</td>
                <td style={{ padding: '10px 14px', fontWeight: 700, color: '#16a34a', fontSize: 15 }}>{money(totalAllocated)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {/* ALLOCATION: uncomment to restore allocate modal
      {allocModal && (
        <AllocateToPurchaseModal
          payment={payment}
          unallocated={Number(payment.amount) - totalAllocated}
          vendorId={payment.vendor_id}
          onClose={() => setAllocModal(false)}
          onSaved={fetchData}
        />
      )} */}
    </div>
  );
}